package nl.volleyplanner.web.controller.thuisprogramma.version_selector

import kotlinx.browser.document
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.dom.addClass
import kotlinx.dom.clear
import kotlinx.dom.removeClass
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.planner.PlanningProblem
import nl.volleyplanner.api.planner.PlanningProblemVersion
import nl.volleyplanner.api.planner.UpdatePlanningProblemVersionRequest
import nl.volleyplanner.web.ApiDataStateFlow
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.component.*
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.controller.thuisprogramma.ThuisprogrammaDataManager
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import nl.volleyplanner.web.util.combineAndLaunch
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.events.Event
import org.w3c.xhr.FormData

data class VersionToEdit(
    val problem: PlanningProblem,
    val version: PlanningProblemVersion?
)

class EditVersionModalComponent(
    private val thuisprogrammaDataManager: ThuisprogrammaDataManager
) : ModalComponent("Versie bewerken", ModalSize.Large) {
    override val footerButtons = listOf(
        BootstrapButton("Annuleren", ButtonVariant.Secondary, null, mapOf("data-bs-dismiss" to "modal")),
        BootstrapButton("Opslaan", ButtonVariant.Primary, {
            it.preventDefault()

            // TODO: kill me now. Need to completely revamp the Modal component to properly submit forms through modal buttons
            //  super dirty workaround. Firing a submit event to trigger the form submit functions
            //  (cannot call submit directly as this would not use the custom submit functions and events)
            //  Yes kill me more: this is directly copied from another place where we already do this, making the required refactor only bigger....
            val formElement = document.getElementById("vp-edit-problem-version-form")!! as HTMLFormElement
            formElement.dispatchEvent(Event("submit"))
        }
        )
    )

    private val plannerConfigurationsState = ApiDataStateFlow(
        "PlannerConfigurationsData",
        AuthLevel.AdminUser,
        contextFlow = WebUserAndOrganisationService.selectedOrganisationState
    ) { organisationContext ->
        organisationContext?.volleyplannerApiClient?.organisation?.plannerConfiguration?.listPlannerConfigurations()
    }

    private val versionToEditState = MutableStateFlow<VersionToEdit?>(null)

    override suspend fun getState() = listOf(versionToEditState, plannerConfigurationsState.subscribe()).combineAndLaunch(componentScope)

    fun show(problem: PlanningProblem, versionToEdit: PlanningProblemVersion?) {
        versionToEditState.value = VersionToEdit(problem, versionToEdit)
        show()
    }

    override fun FlowContent.renderBody() {
        val plannerConfigurations = plannerConfigurationsState.value
        val stateHolder = versionToEditState.value

        if (plannerConfigurations == null || stateHolder == null) {
            spinner()
            return
        }
        val problemToEdit = stateHolder.problem
        val versionToEdit = stateHolder.version
        validatingForm(
            customValidityFunction = { formElement ->
            },
            validSubmitFunction = { event, formElement ->
                event.preventDefault()
                val statusElement = document.getElementById("vp-create-version-form-status")!! as HTMLElement

                statusElement.removeClass("d-none")
                statusElement.clear()

                statusElement.append(document.create.div(classes = "alert alert-secondary d-flex align-items-center") {
                    role = "alert"
                    spinner()
                    +"Gegevens opslaan..."
                })

                val formData = FormData(formElement)
                //val versionName = formData.get("vp-version-name").trim()
                val plannerConfigurationIdentifier = formData.get("vp-planner-configuration") as String

                val updateRequest = UpdatePlanningProblemVersionRequest(
                    problemToEdit.slug,
                    versionToEdit?.slug,
                    plannerConfigurationIdentifier
                )
                VolleyPlannerApplication.volleyplannerApiClient
                    .organisation
                    .plannerConfiguration
                    .updateProblemVersion(updateRequest)
                if (versionToEdit == null) {
                    thuisprogrammaDataManager.problemData.problemDataFlow.refresh()
                } else {
                    thuisprogrammaDataManager.problemData.problemVersionsDataFlow.refresh()
                }
                thuisprogrammaDataManager.problemData.getVersionData(versionToEdit?.slug).thuisprogrammaStatisticsState.refresh()
                statusElement.addClass("d-none")
                hide()
            },
            onSubmitErrorFunction = { throwable, formElement ->
                val statusElement = document.getElementById("vp-create-version-form-status")!! as HTMLElement
                statusElement.removeClass("d-none")
                statusElement.clear()

                statusElement.append(document.create.div (classes = "alert alert-danger d-flex align-items-center") {
                    role = "alert"
                    icon("bi-exclamation-triangle")
                    +"Fout bij opslaan gegevens: ${throwable.message}"
                })
            }
        ) {
            id = "vp-edit-problem-version-form"

            div(classes = "d-none") {
                id = "vp-edit-version-form-status"
            }
            div(classes = "p-3 mb-2") {
                fieldSet {
                    // Heading
                    legend(classes = "h6 mb-4") {
                        +"Versie bewerken: ${versionToEdit?.name ?: "Gepubliceerd"}"
                    }

                    // Version Name Input
                    div(classes = "row g-3 mb-4 align-items-center") {
                        label(classes = "col-12 col-lg-4 col-form-label") {
                            htmlFor = "vp-version-name"
                            +"Naam"
                        }
                        div(classes = "col-12 col-lg-8 col-xl-6") {
                            input(type = InputType.text, classes = "form-control") {
                                id = "vp-version-name"
                                name = "vp-version-name"
                                required = true
                                disabled = true

                                value = versionToEdit?.name ?: "Gepubliceerd"
                            }
                            div(classes = "invalid-feedback") {
                                +"Een naam is verplicht."
                            }
                        }
                    }

                    // Planner Configuration Select
                    div(classes = "row g-3 mb-4 align-items-center") {
                        label(classes = "col-12 col-lg-4 col-form-label") {
                            htmlFor = "vp-planner-configuration"
                            +"Planner configuratie"
                        }
                        div(classes = "col-12 col-lg-8 col-xl-6") {
                            select(classes = "form-select") {
                                id = "vp-planner-configuration"
                                name = "vp-planner-configuration"
                                required = true

                                val selectedConfigurationIdentifier = if (versionToEdit == null) {
                                    problemToEdit.plannerConfigurationMetadata.identifier
                                } else {
                                    versionToEdit.plannerConfigurationMetadata.identifier
                                }
                                plannerConfigurations.forEach { configuration ->
                                    option {
                                        selected = configuration.identifier == selectedConfigurationIdentifier

                                        value = configuration.identifier
                                        +configuration.name
                                    }
                                }
                            }
                            div(classes = "form-text") {
                                +"De configuratie die wordt gebruikt bij het draaien van de planner voor deze versie en om scores/statistieken te berekenen. "
                                +"Momenteel is enkel de 'standaard' configuratie beschikbaar."
                            }
                            div(classes = "invalid-feedback") {
                                +"Een planner configuratie is verplicht."
                            }
                        }
                    }
                }
            }
        }
    }
}